import type {
  MyShareAccess,
  SharePermissionType,
} from 'features/chat/agent-chat-module/types/agent-sharing.type';
import {
  canEditResource,
  isOwner,
  resolveCreatedBy,
  type OwnedResource,
} from 'lib/ownership';

/** Snake_case shape from agent list/detail (`my_share_access` dump by_alias=False). */
export type AgentMyShareAccessApi = {
  is_owner?: boolean;
  permission_type?: SharePermissionType | null;
  can_share?: boolean;
  can_edit_config?: boolean;
  can_manage_subagents?: boolean;
  can_manage_skills?: boolean;
  can_manage_tools?: boolean;
  can_manage_kb?: boolean;
  // camelCase fallbacks if a gateway remaps
  isOwner?: boolean;
  permissionType?: SharePermissionType | null;
  canShare?: boolean;
  canEditConfig?: boolean;
};

export type AgentShareFields = OwnedResource & {
  visibility?: string | null;
  is_shared?: boolean;
  isShared?: boolean;
  participants?: unknown[] | null;
  my_share_access?: AgentMyShareAccessApi | null;
  myShareAccess?: AgentMyShareAccessApi | MyShareAccess | null;
};

export function normalizeMyShareAccess(
  raw?: AgentMyShareAccessApi | MyShareAccess | null
): MyShareAccess | undefined {
  if (!raw) return undefined;
  const r = raw as AgentMyShareAccessApi & MyShareAccess;
  return {
    isOwner: !!(r.isOwner ?? r.is_owner),
    permissionType: (r.permissionType ?? r.permission_type) || undefined,
    canShare: !!(r.canShare ?? r.can_share),
    canEditConfig: !!(r.canEditConfig ?? r.can_edit_config),
    canManageSubagents: !!(
      (r as MyShareAccess).canManageSubagents ?? r.can_manage_subagents
    ),
    canManageSkills: !!((r as MyShareAccess).canManageSkills ?? r.can_manage_skills),
    canManageTools: !!((r as MyShareAccess).canManageTools ?? r.can_manage_tools),
    canManageKb: !!((r as MyShareAccess).canManageKb ?? r.can_manage_kb),
  };
}

/**
 * Card/edit UI only: show the Shared badge when this private agent was shared
 * with the current user. Never for owners. Never for public agents.
 * Do not use this for edit/permission checks — use canEdit* / isAgentOwnerAccess.
 */
export function shouldShowSharedBadge(
  agent: AgentShareFields | null | undefined,
  currentUserId?: string | null
): boolean {
  if (!agent) return false;
  if ((agent.visibility || '').toLowerCase() !== 'private') return false;

  const access = normalizeMyShareAccess(agent.my_share_access ?? agent.myShareAccess);
  if (access?.isOwner) return false;
  if (access?.permissionType) return true;

  const owner = isOwner(resolveCreatedBy(agent), currentUserId);
  if (owner) return false;

  // Fallback when my_share_access is missing: private + not owner ≈ shared with me
  return true;
}

/** True when the BE sent a share-access block (vs. org-admin / owner rights). */
export function hasAgentShareAccess(
  agent: AgentShareFields | null | undefined
): boolean {
  return !!(agent?.my_share_access ?? agent?.myShareAccess);
}

/**
 * Effective edit rights: owner OR share editor (`can_edit_config`).
 * Without my_share_access, defers to the server `can_edit` flag (owner or org
 * admin), then to CreatedBy ownership (pre-BE).
 */
export function canEditAgentConfig(
  agent: AgentShareFields | null | undefined,
  currentUserId?: string | null
): boolean {
  const access = normalizeMyShareAccess(agent?.my_share_access ?? agent?.myShareAccess);
  if (access) {
    return access.isOwner || access.canEditConfig;
  }
  return canEditResource(agent, currentUserId);
}

/**
 * Who may configure this agent as their own organization's widget.
 *
 * The server decides and reports it as `can_configure_widget`: the creator inside the
 * agent's own org, or an org admin / explicit editor for an agent shared in from
 * another one. Never re-derived here -- the rule spends an organization's credits, so
 * the two consoles must not drift from what the mutation guard enforces. The fallback
 * only covers a server that predates the field.
 */
export function canConfigureAgentWidget(
  agent: (AgentShareFields & { can_configure_widget?: boolean; canConfigureWidget?: boolean }) | null | undefined,
  currentUserId?: string | null
): boolean {
  const told = agent?.can_configure_widget ?? agent?.canConfigureWidget;
  if (typeof told === 'boolean') return told;
  return canEditAgentConfig(agent, currentUserId);
}

export function isAgentOwnerAccess(
  agent: AgentShareFields | null | undefined,
  currentUserId?: string | null
): boolean {
  const access = normalizeMyShareAccess(agent?.my_share_access ?? agent?.myShareAccess);
  if (access) return access.isOwner;
  return isOwner(resolveCreatedBy(agent), currentUserId);
}

type ManageFlag = keyof Pick<
  MyShareAccess,
  'canManageSubagents' | 'canManageSkills' | 'canManageTools' | 'canManageKb'
>;

function canManageResource(
  agent: AgentShareFields | null | undefined,
  currentUserId: string | null | undefined,
  flag: ManageFlag
): boolean {
  const access = normalizeMyShareAccess(agent?.my_share_access ?? agent?.myShareAccess);
  if (access) {
    return access.isOwner || !!access[flag];
  }
  return canEditResource(agent, currentUserId);
}

export function canManageSubagents(
  agent: AgentShareFields | null | undefined,
  currentUserId?: string | null
): boolean {
  return canManageResource(agent, currentUserId, 'canManageSubagents');
}

export function canManageSkills(
  agent: AgentShareFields | null | undefined,
  currentUserId?: string | null
): boolean {
  return canManageResource(agent, currentUserId, 'canManageSkills');
}

export function canManageTools(
  agent: AgentShareFields | null | undefined,
  currentUserId?: string | null
): boolean {
  return canManageResource(agent, currentUserId, 'canManageTools');
}

export function canManageKb(
  agent: AgentShareFields | null | undefined,
  currentUserId?: string | null
): boolean {
  return canManageResource(agent, currentUserId, 'canManageKb');
}
